import { NOTIFICATIONS_FILTER_BY_STATE, NOTIFICATIONS_FILTER_BY_TYPE } from 'vd-constants';

const STATE = NOTIFICATIONS_FILTER_BY_STATE;
const TYPE = NOTIFICATIONS_FILTER_BY_TYPE;

export const FILTER_COUNT_INTENT = {
  archive: 'archive',
  read: 'read',
};

export function createFilterCountMemory() {
  return {
    counts: null,
    snapshot: null,
    known: new Map(),
    archivedIds: new Set(),
    errorQueryCount: null,
    intents: new Map(),
    bulk: null,
    globalBulk: null,
  };
}

// Cutoff bulk actions reload only the open filter, so chip totals cannot be
// derived from that list alone.
export function applyGlobalBulkResult(memory, intent) {
  if (!memory.counts) memory.counts = {};
  const counts = memory.counts;
  const all = counts[STATE.all] ?? 0;

  if (intent === FILTER_COUNT_INTENT.archive) {
    const previousArchived = counts[STATE.archived] ?? 0;
    counts[STATE.all] = 0;
    counts[STATE.read] = 0;
    counts[STATE.unread] = 0;
    counts[STATE.error] = 0;
    counts[TYPE.alarm] = 0;
    counts[TYPE.clip] = 0;
    counts[TYPE.event] = 0;
    counts[STATE.archived] = previousArchived + all;
    memory.known.clear();
    memory.errorQueryCount = 0;
  } else if (intent === FILTER_COUNT_INTENT.read) {
    counts[STATE.unread] = 0;
    counts[STATE.read] = all;
    // A read cutoff does not change Firestore error-state rows.
    for (const [id, item] of memory.known) {
      if (!item.isRead) memory.known.set(id, { ...item, isRead: true });
    }
  }

  memory.intents.clear();
  memory.bulk = null;
  memory.globalBulk = null;
}

function isRead(notification, readCutoffDate) {
  const { created, state } = notification || {};
  return created < readCutoffDate || state === STATE.read;
}

function isError(notification, notificationErrorIndicies) {
  const { id, state } = notification || {};
  return state === STATE.error || !!notificationErrorIndicies?.[id];
}

function toItem(notification, readCutoffDate, notificationErrorIndicies) {
  return {
    id: notification.id,
    type: notification.type,
    isRead: isRead(notification, readCutoffDate),
    isError: isError(notification, notificationErrorIndicies),
  };
}

function indexList(notifications, readCutoffDate, notificationErrorIndicies) {
  const byId = new Map();
  notifications.forEach((notification) => {
    if (notification?.id == null) return;
    byId.set(notification.id, toItem(notification, readCutoffDate, notificationErrorIndicies));
  });
  return byId;
}

function countActiveSet(notifications, readCutoffDate, notificationErrorIndicies) {
  const counts = {
    [STATE.all]: notifications.length,
    [STATE.read]: 0,
    [STATE.unread]: 0,
    [TYPE.alarm]: 0,
    [TYPE.clip]: 0,
    [TYPE.event]: 0,
  };

  notifications.forEach((notification) => {
    const item = toItem(notification, readCutoffDate, notificationErrorIndicies);
    counts[item.isRead ? STATE.read : STATE.unread] += 1;
    if (item.type && counts[item.type] != null) counts[item.type] += 1;
  });

  return counts;
}

function listsEqual(previous, next) {
  if (!previous || previous.size !== next.size) return false;

  for (const [id, item] of next) {
    const prior = previous.get(id);
    if (!prior) return false;
    if (
      prior.isRead !== item.isRead ||
      prior.isError !== item.isError ||
      prior.type !== item.type
    ) {
      return false;
    }
  }

  return true;
}

function bump(counts, key, delta) {
  if (!key || delta === 0 || counts[key] == null) return;
  counts[key] = Math.max(0, counts[key] + delta);
}

function bumpArchived(counts, delta) {
  if (delta === 0) return;
  if (counts[STATE.archived] == null) {
    if (delta > 0) counts[STATE.archived] = delta;
    return;
  }
  counts[STATE.archived] = Math.max(0, counts[STATE.archived] + delta);
}

function removeFromActive(counts, memory, item, skipKey) {
  memory.known.delete(item.id);
  memory.archivedIds.add(item.id);
  if (skipKey !== STATE.all) bump(counts, STATE.all, -1);
  const stateKey = item.isRead ? STATE.read : STATE.unread;
  if (skipKey !== stateKey) bump(counts, stateKey, -1);
  if (item.isError) bump(counts, STATE.error, -1);
  if (skipKey !== item.type) bump(counts, item.type, -1);
  if (skipKey !== STATE.archived) bumpArchived(counts, 1);
}

function moveToRead(counts, memory, item, skipKey) {
  memory.known.set(item.id, { ...item, isRead: true });
  if (skipKey !== STATE.unread) bump(counts, STATE.unread, -1);
  if (skipKey !== STATE.read) bump(counts, STATE.read, 1);
}

function addToActive(counts, memory, item, skipKey) {
  const fromArchived = memory.archivedIds.delete(item.id);
  memory.known.set(item.id, item);
  if (fromArchived) bumpArchived(counts, -1);
  if (skipKey !== STATE.all) bump(counts, STATE.all, 1);
  const stateKey = item.isRead ? STATE.read : STATE.unread;
  if (skipKey !== stateKey) bump(counts, stateKey, 1);
  if (item.isError && skipKey !== STATE.error) bump(counts, STATE.error, 1);
  if (skipKey !== item.type) bump(counts, item.type, 1);
}

function applyStay(counts, memory, prior, item, filterBy) {
  if (prior.isRead !== item.isRead) {
    const from = prior.isRead ? STATE.read : STATE.unread;
    const to = item.isRead ? STATE.read : STATE.unread;
    if (filterBy !== from) bump(counts, from, -1);
    if (filterBy !== to) bump(counts, to, 1);
  }
  if (prior.isError !== item.isError && filterBy !== STATE.error) {
    bump(counts, STATE.error, item.isError ? 1 : -1);
  }
  if (prior.type !== item.type) {
    if (filterBy !== prior.type) bump(counts, prior.type, -1);
    if (filterBy !== item.type) bump(counts, item.type, 1);
  }
  memory.known.set(item.id, item);
}

function bulkIntentFor(memory, id) {
  if (!memory.bulk?.ids?.has(id)) return;
  return memory.bulk.intent;
}

function leaveFilter(counts, memory, item, filterBy) {
  if (filterBy === STATE.archived) {
    memory.archivedIds.delete(item.id);
    memory.known.delete(item.id);
    return;
  }

  if (filterBy === STATE.unread) {
    const intent = memory.intents.get(item.id) || bulkIntentFor(memory, item.id);
    if (intent === FILTER_COUNT_INTENT.archive) {
      removeFromActive(counts, memory, item, filterBy);
      return;
    }
    if (intent === FILTER_COUNT_INTENT.read) {
      moveToRead(counts, memory, item, filterBy);
    }
    return;
  }

  removeFromActive(counts, memory, item, filterBy);
}

function enterFilter(counts, memory, item, filterBy) {
  const prior = memory.known.get(item.id);

  if (filterBy === STATE.archived) {
    if (prior) removeFromActive(counts, memory, prior, filterBy);
    else memory.archivedIds.add(item.id);
    return;
  }

  if (filterBy === STATE.read && prior && !prior.isRead) {
    moveToRead(counts, memory, { ...item, isRead: true }, filterBy);
    return;
  }

  addToActive(counts, memory, item, filterBy);
}

function applyActiveSet(
  memory,
  notifications,
  byId,
  previous,
  readCutoffDate,
  notificationErrorIndicies,
) {
  const counts = countActiveSet(notifications, readCutoffDate, notificationErrorIndicies);
  // Archived items are outside the active set. Keep that count across All.
  let archived = memory.counts?.[STATE.archived] ?? null;
  let returned = 0;

  if (previous) {
    let removed = 0;
    for (const [id] of previous) {
      if (byId.has(id)) continue;
      const intent = memory.intents.get(id) || bulkIntentFor(memory, id);
      memory.intents.delete(id);
      memory.bulk?.ids?.delete(id);
      // Playback failure flags this id, then its state becomes error and the All
      // query drops it. That is not an archive. An archive intent still wins.
      if (intent !== FILTER_COUNT_INTENT.archive && notificationErrorIndicies?.[id]) {
        memory.known.delete(id);
        if (memory.errorQueryCount != null) memory.errorQueryCount += 1;
        continue;
      }
      removed += 1;
      memory.archivedIds.add(id);
      memory.known.delete(id);
    }
    for (const [id, item] of byId) {
      memory.known.set(id, item);
      if (memory.archivedIds.delete(id)) returned += 1;
    }
    if (memory.bulk && memory.bulk.ids.size === 0) memory.bulk = null;
    if (archived != null || removed > 0 || returned > 0) {
      archived = Math.max(0, (archived ?? 0) + removed - returned);
    }
  } else {
    memory.known = new Map(byId);
    for (const id of byId.keys()) {
      if (memory.archivedIds.delete(id)) returned += 1;
    }
    if (returned > 0 && archived != null) {
      archived = Math.max(0, archived - returned);
    }
  }

  if (archived != null) counts[STATE.archived] = archived;
  // The Error query is a separate list. Keep that count across All.
  if (memory.errorQueryCount != null) counts[STATE.error] = memory.errorQueryCount;
  memory.counts = counts;
}

function applySameFilter(memory, notifications, byId, previous, filterBy) {
  if (!memory.counts) memory.counts = {};
  const counts = memory.counts;

  for (const [id, prior] of previous) {
    if (!byId.has(id)) leaveFilter(counts, memory, prior, filterBy);
  }

  for (const [id, item] of byId) {
    const prior = previous.get(id);
    if (!prior) enterFilter(counts, memory, item, filterBy);
    else if (filterBy !== STATE.archived) applyStay(counts, memory, prior, item, filterBy);
  }

  counts[filterBy] = notifications.length;
}

export function rememberFilterCounts(memory, params) {
  const {
    notifications,
    filterBy,
    isLoading,
    readCutoffDate,
    notificationErrorIndicies = {},
  } = params;

  if (isLoading || !notifications) return memory.counts;

  const byId = indexList(notifications, readCutoffDate, notificationErrorIndicies);
  const previous = memory.snapshot?.filterBy === filterBy ? memory.snapshot.byId : null;

  if (memory.globalBulk) {
    const openListChanged = !previous || !listsEqual(previous, byId);
    applyGlobalBulkResult(memory, memory.globalBulk);
    if (!memory.counts) memory.counts = {};
    // An unchanged open list still hides rows in other filters. Keep the global
    // chip result instead of replacing it with that list's length.
    if (openListChanged) memory.counts[filterBy] = notifications.length;
    if (filterBy === STATE.error) {
      memory.errorQueryCount = notifications.length;
      memory.counts[STATE.error] = notifications.length;
    }
    memory.snapshot = { filterBy, byId };
    return memory.counts;
  }

  if (filterBy === STATE.error) {
    if (!memory.counts) memory.counts = {};
    if (previous) {
      for (const [id, prior] of previous) {
        if (byId.has(id)) continue;
        const intent = memory.intents.get(id) || bulkIntentFor(memory, id);
        memory.intents.delete(id);
        memory.bulk?.ids?.delete(id);
        if (intent === FILTER_COUNT_INTENT.archive) {
          if (memory.known.has(id)) {
            removeFromActive(memory.counts, memory, memory.known.get(id), filterBy);
          } else if (!memory.archivedIds.has(id)) {
            memory.archivedIds.add(id);
            bumpArchived(memory.counts, 1);
          }
        } else if (intent === FILTER_COUNT_INTENT.read) {
          const known = memory.known.get(id);
          if (known) {
            if (!known.isRead) {
              moveToRead(
                memory.counts,
                memory,
                { ...known, isRead: true, isError: false },
                filterBy,
              );
            } else {
              memory.known.set(id, { ...known, isError: false });
            }
          } else {
            addToActive(
              memory.counts,
              memory,
              { ...prior, isRead: true, isError: false },
              filterBy,
            );
          }
        }
      }
      if (memory.bulk && memory.bulk.ids.size === 0) memory.bulk = null;
    }
    memory.errorQueryCount = notifications.length;
    memory.counts[STATE.error] = memory.errorQueryCount;
    memory.snapshot = { filterBy, byId };
    return memory.counts;
  }

  if (filterBy === STATE.all) {
    applyActiveSet(
      memory,
      notifications,
      byId,
      previous,
      readCutoffDate,
      notificationErrorIndicies,
    );
  } else if (previous) {
    applySameFilter(memory, notifications, byId, previous, filterBy);
  } else {
    if (!memory.counts) memory.counts = {};
    memory.counts[filterBy] = notifications.length;
  }

  if (previous && !listsEqual(previous, byId)) {
    for (const [id] of previous) {
      if (byId.has(id)) continue;
      memory.intents.delete(id);
      memory.bulk?.ids?.delete(id);
    }
    if (memory.bulk && memory.bulk.ids.size === 0) {
      memory.bulk = null;
    }
  }

  memory.snapshot = { filterBy, byId };
  return memory.counts;
}
